import { deserializeMd as plateDeserializeMd } from '@udecode/plate-markdown';
import { visit } from 'unist-util-visit';
import { visitParents } from 'unist-util-visit-parents';
import { processTextWithMentions } from '../Mention/mentionUtils';
const recursiveMarkUnderline = (node) => {
    if (node.type === 'text') {
        return {
            ...node,
            underline: true
        };
    }
    else if (node.children) {
        return {
            ...node,
            children: node.children.map(recursiveMarkUnderline)
        };
    }
    return node;
};
const remarkUnderlinePlugin = (_options) => {
    return (tree) => {
        visitParents(tree, (node, ancestors) => {
            if (node.type === 'html' && node.value === '<u>') {
                const parent = ancestors[ancestors.length - 1]; // the node which we want to locate for u tag
                const index = parent.children.indexOf(node);
                // find the closing tag in parent
                const closingTagIndex = parent.children.findIndex((child) => child.type === 'html' && child.value === '</u>');
                if (closingTagIndex === -1)
                    return;
                // mark all siblings as underlined
                const allSiblings = parent.children.slice(index, closingTagIndex + 1);
                const underlinedSiblings = allSiblings.map((sibling) => recursiveMarkUnderline(sibling));
                parent.children.splice(index, allSiblings.length, ...underlinedSiblings);
                // remove the u tag
                parent.children.splice(index, 1);
                // remove the closing tag
                parent.children.splice(closingTagIndex - 1, 1);
            }
        });
        return tree;
    };
};
// Remark plugin that adds mention metadata to text nodes
const remarkMentionAttributesPlugin = () => {
    return (tree) => {
        visit(tree, 'text', (node, index, parent) => {
            if (!parent || index === null || index === undefined)
                return;
            const text = node.value;
            const segments = processTextWithMentions(text);
            // Always use map - it handles all cases uniformly
            const newNodes = segments.map((segment) => {
                const baseNode = {
                    type: 'text',
                    value: segment.text
                };
                if (segment.isMention) {
                    baseNode.mention = true;
                    baseNode.mentionValue = segment.mentionValue;
                }
                return baseNode;
            });
            // Replace the original node with the new nodes
            parent.children.splice(index, 1, ...newNodes);
        });
        return tree;
    };
};
/**
 * Pre-processes markdown to remove formatting markers around images.
 * Images wrapped in formatting markers like `*![](url)*`, `**![](url)**`, `~~![](url)~~`, etc.
 * are not valid markdown syntax and can cause rendering issues. This function strips any
 * formatting markers (asterisks, underscores, backticks, tildes) that wrap images.
 *
 * Formatting markers handled:
 * - `*` (italic)
 * - `**` (bold)
 * - `_` (italic alternate)
 * - `__` (bold alternate)
 * - `` ` `` (code)
 * - `~~` (strikethrough)
 * - Any combination of the above
 */
function preprocessMarkdownForImages(value) {
    if (!value)
        return value;
    // General pattern that matches any sequence of formatting markers before and after images
    // Formatting characters: *, _, `, ~
    // This handles:
    // - Symmetric cases: *![](url)*, **![](url)**, _![](url)_, __![](url)__, ~~![](url)~~
    // - Asymmetric cases: *_![](url)_*, *__![](url)__*, etc.
    // - Mixed markers: any combination of formatting characters
    //
    // Pattern breakdown:
    // - ([*_`~]+) - capture group 1: one or more formatting characters before image
    // - !\[([^\]]*)\]\(([^)]+)\) - image syntax with alt text and URL
    // - ([*_`~]+) - capture group 4: one or more formatting characters after image
    //
    // We match any formatting markers before and after, regardless of whether they match
    value = value.replace(/([*_`~]+)!\[([^\]]*)\]\(([^)]+)\)([*_`~]+)/g, '![$2]($3)');
    return value;
}
export function deserializeMd(editor, value) {
    // Pre-process markdown to remove formatting markers around images
    const preprocessedValue = preprocessMarkdownForImages(value);
    const deserializedNodes = plateDeserializeMd(editor, preprocessedValue, {
        processor: (processor) => {
            processor.use(remarkMentionAttributesPlugin);
            processor.use(remarkUnderlinePlugin, {});
            return processor;
        }
    });
    // Transform text nodes with mention metadata into proper mention elements
    // TODO: Restructure this to be a common utility with transformations as arguments
    // support. So that new transformations can be added in the same loop cycle.
    const transformedNodes = transformMentionTextAndInvalidAnchorNodes(deserializedNodes);
    return transformedNodes;
}
// Transform text nodes with mention metadata into proper mention elements
function transformMentionTextAndInvalidAnchorNodes(nodes) {
    return nodes.map((node) => {
        if ('children' in node && node.children && Array.isArray(node.children)) {
            // Recursively transform children
            const transformedChildren = transformMentionTextAndInvalidAnchorNodes(node.children);
            // Process children to convert mention text nodes to mention elements
            const newChildren = [];
            for (const child of transformedChildren) {
                if ('text' in child && child.mention && child.mentionValue) {
                    // Convert mention text node to mention element
                    const mentionElement = {
                        type: 'mention',
                        value: child.mentionValue,
                        children: [{ text: '' }]
                    };
                    newChildren.push(mentionElement);
                }
                else {
                    newChildren.push(child);
                }
            }
            // This is done because we've hijacked the `a` tag for LiveSpec markers/placeholders.
            if (node.type === 'a') {
                if (!hasValidUrl(node.url)) {
                    const linkText = node.children?.[0]?.text ?? '';
                    return { text: `[${linkText}](${node.url})` };
                }
            }
            return {
                ...node,
                children: newChildren
            };
        }
        return node;
    });
}
function hasValidUrl(url) {
    return (typeof url === 'string' &&
        url.trim().length > 0 &&
        (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')));
}
